import { getPendingApprovals } from '@/mocks/api';
import { useAsync } from '@/hooks/useAsync';
import { resolveContext } from '@/state/session';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import { Amount, Initials, ListRow, StatusBadge } from '@/components/banking';
import { EmptyState, ErrorState, LoadingRegion, RowSkeleton } from '@/components/feedback';
import { formatDayGroup } from '@/services/formatters';

/**
 * Pending approvals — WF-SMEM-07.
 *
 * Only requests this user is actually an approver for are listed; the adapter
 * filters by `approverIds`, so an unauthorised user cannot see the queue even
 * with a direct link.
 */
export function ApprovalsPage() {
  const resolved = resolveContext();
  const companyId = resolved?.company.id ?? null;
  const userId = resolved?.user.id ?? null;

  const approvals = useAsync(async () => {
    if (!companyId || !userId) return [];
    return (await getPendingApprovals(companyId, userId)).data;
  }, [companyId, userId]);

  if (!resolved) return null;

  const list = approvals.data ?? [];

  return (
    <>
      <MobileHeader title="Approvals" root />
      <div className="tm-stack">
        {approvals.status === 'loading' ? (
          <LoadingRegion label="Loading approvals">
            <div className="tm-card tm-card--flush">
              <RowSkeleton count={3} />
            </div>
          </LoadingRegion>
        ) : approvals.status === 'error' ? (
          <ErrorState error={approvals.error} onRetry={approvals.reload} />
        ) : list.length === 0 ? (
          <EmptyState
            title="Nothing to approve"
            body="When a payment needs your decision it will appear here."
            icon={
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 6.5 9 17.5l-5-5" />
              </svg>
            }
          />
        ) : (
          <>
            <p className="tm-muted" role="status">
              {list.length} payment{list.length === 1 ? '' : 's'} waiting for your decision
            </p>
            <div className="tm-card tm-card--flush">
              <ul className="tm-rows">
                {list.map(({ approval, payment, beneficiary }) => (
                  <li key={approval.id}>
                    <ListRow
                      to={`/mobile/approvals/${approval.id}`}
                      leading={<Initials name={beneficiary?.name ?? 'Payee'} />}
                      title={beneficiary?.name ?? 'Payee'}
                      meta={`${payment.reference} · raised ${formatDayGroup(payment.createdAt)}`}
                      end={
                        <span style={{ display: 'grid', gap: 4, justifyItems: 'end' }}>
                          <Amount minorUnits={payment.amount} currency={payment.currency} />
                          {beneficiary && beneficiary.verificationResult !== 'match' ? (
                            <StatusBadge status={beneficiary.verificationResult} />
                          ) : null}
                        </span>
                      }
                      ariaLabel={`Approve or reject a payment to ${beneficiary?.name ?? 'a payee'}, reference ${payment.reference}`}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    </>
  );
}
